import type { IAnswer, IQuestion } from "@/services/SuitabilityTestsService";

/**
 * Adapta las respuestas precargadas al formulario.
 * El radio pintado usa res y order (texto). La validez inicial del radio usa value y order.
 * Sin value, la opción sale marcada y "Enviar" sigue deshabilitado.
 */
export function prepareSuitabilityInitialAnswers(questions: IQuestion[], initialAnswers: IAnswer[]): IAnswer[]
{
    const questionsById = new Map(questions.map((question) => [question._id, question]));
    return initialAnswers.map((answer) =>
    {
        const question = questionsById.get(answer.originalQuestionId || answer.questionId);
        const order = answer.order == null || answer.order === "" ? answer.order : String(answer.order);
        if(question?.questionType !== "radio")
        {
            return{
                ...answer,
                order: order,
            };
        }

        const value = answer.value != null ? answer.value : answer.res;
        const res = answer.res != null ? answer.res : value;
        return{
            ...answer,
            value: value,
            res: res,
            order: order,
        };
    });
}

/**
 * Respuestas tal y como se envían al crear el test. El value de los radio solo sirve para la validación inicial
 * de la precarga: el flujo normal nunca lo envía y, si el usuario cambia la opción, se queda con la anterior
 * (handleTestChange solo actualiza res y order). Se quita para que Finametrix reciba lo mismo de siempre.
 */
export function buildAnswersForSubmit(questions: IQuestion[] | undefined, answers: IAnswer[] | undefined): IAnswer[]
{
    const radioQuestionIds = new Set((questions ?? [])
        .filter((question) => question.questionType === "radio")
        .map((question) => question._id));

    return (answers ?? []).map((answer) =>
    {
        if(!radioQuestionIds.has(answer.questionId) || answer.value === undefined)
        {
            return answer;
        }

        const withoutValue: IAnswer = { ...answer };
        delete withoutValue.value;
        return withoutValue;
    });
}

/** Radios sin respuesta casada. El formulario añade una vacía para poder marcarlas; no duplica las precargadas. */
export function buildMissingRadioAnswers(questions: IQuestion[], answers: IAnswer[]): IAnswer[]
{
    const answeredQuestionIds = new Set(answers.map((answer) => answer.questionId));
    const missing: IAnswer[] = [];
    questions.forEach((question) =>
    {
        if(question.questionType === "radio" && !answeredQuestionIds.has(question._id))
        {
            missing.push(
            {
                questionId: question._id,
                key: question.key,
            });
        }
    });
    return missing;
}

/**
 * Estado inicial de cada pregunta para habilitar "Enviar".
 * Sin precarga se conserva el criterio anterior: sin dependency queda en false; con dependency, en true.
 * Con precarga, una pregunta ya contestada arranca en true. Un radio dependiente visible y vacío arranca
 * en false; si la respuesta del padre lo oculta, no bloquea el envío.
 */
export function buildSuitabilityTestForm(questions: IQuestion[], initialAnswers?: IAnswer[]): Record<string, boolean>
{
    return questions.reduce<Record<string, boolean>>((form, question) =>
    {
        if(initialAnswers === undefined)
        {
            form[question._id] = question.dependency !== undefined;
            return form;
        }

        form[question._id] = isQuestionInitiallyValid(question, questions, initialAnswers);
        return form;
    }, {});
}

function isQuestionInitiallyValid(question: IQuestion, questions: IQuestion[], answers: IAnswer[]): boolean
{
    if(question.questionType === "checkbox" || question.questionType === "table")
    {
        return true;
    }

    if(question.questionType === "multi")
    {
        return multiPercentsSumTo100(question, answers);
    }

    if(question.questionType !== "radio")
    {
        return question.dependency !== undefined;
    }

    if(isDependentRadioHidden(question, questions, answers))
    {
        return true;
    }

    return isRadioAnswerComplete(answers.find((answer) => answer.questionId === question._id));
}

function isRadioAnswerComplete(answer: IAnswer | undefined): boolean
{
    if(!answer || answer.order == null || answer.order === "")
    {
        return false;
    }

    const value = answer.value != null ? answer.value : answer.res;
    return value !== undefined && value !== null;
}

/** Igual que el render: un radio dependiente se oculta cuando la respuesta del padre no tiene res. */
function isDependentRadioHidden(question: IQuestion, questions: IQuestion[], answers: IAnswer[]): boolean
{
    if(question.dependency === undefined || question.dependency === null)
    {
        return false;
    }

    const parent = questions.find((candidate) => candidate.order === question.dependency);
    if(!parent || parent.questionType !== "radio")
    {
        return false;
    }

    const parentAnswer = answers.find((answer) => answer.questionId === parent._id);
    return !parentAnswer?.res;
}

function multiPercentsSumTo100(question: IQuestion, answers: IAnswer[]): boolean
{
    const optionIds = new Set((question.answers ?? []).map((option) => option._id));
    const sum = answers
        .filter((answer) => answer.answerId != null && optionIds.has(answer.answerId))
        .reduce((total, answer) => total + (Number(answer.inputRes) || 0), 0);
    return sum === 100;
}
